import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Autocomplete, Box, Grid, Hidden, TextField, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GoogleMap, InfoWindow, Marker, useLoadScript } from '@react-google-maps/api';
import { Libraries } from '@react-google-maps/api/dist/utils/make-load-script-url';
import parse from 'autosuggest-highlight/parse';
import { get as _get } from 'lodash';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getRentalShops, getRepairShops } from '../../../api/getShopsApi';
import { getAddressDetailsByPlaceId } from '../../../api/placeDetailsApi';
import { getAuthKey, getSessionId, getUrlKey } from '../../../app/store/combinedSelectors';
import { DEFAULT_DISTANCE, DEFAULT_LIST_SIZE_PER_PAGE, DEFAULT_ZOOM } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { DRPShopResponse, ShopsInfo } from '../../../model/shops.response';
import { Copy } from '../../../types/copy';
import { FullAddress, PaginationData, PlaceType, SearchContext } from '../../../types/type';
import { postMessageToParentApp } from '../../../utils/Utilities';
import ErrorBanner from '../../shared/ErrorBanner/ErrorBanner';
import LoadingIndicator from '../../shared/LoadingIndicator/LoadingIndicator';
import BackNavigation from '../BackNavigation/BackNavigation';
import SearchButton from '../SearchButton/SearchButton';
import Address from '../ShopAddress/ShopAddress';
import ShopInfoCard from '../ShopInfo/ShopInfoCard';
import ShopsNotFound from '../ShopsNotFound/ShopsNotFound';

interface OpcoIdInfo {
  copy: Copy;
  opcoId: string;
  searchContext: SearchContext;
  locationSelection: any; //TODO Type this
}

declare let google: any;
const autocompleteService = { current: null };
const libraries: Libraries = ['places'];

const Search = (props: OpcoIdInfo) => {
  const theme: Theme = useTheme();

  // over all State management
  const [distance, setDistance] = useState<string>(DEFAULT_DISTANCE);
  const [address, setAddress] = useState<FullAddress>();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>();
  const [shopSearchResults, setShopSearchResults] = useState<ShopsInfo[] | null>();
  const [showErrorBanner, setShowErrorBanner] = React.useState(false);

  // State management for Autocomplete
  const [value, setValue] = React.useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);

  // State management for Markers
  const [selectedMarker, setSelectedMarker] = React.useState<ShopsInfo | null>(null);

  // State management for Card display, card selection and Pagination
  const [shopResultsToShow, setShopResultsToShow] = useState<ShopsInfo[] | null>();
  const [paginationData, setPaginationData] = useState<PaginationData>({
    pageCount: 0,
    starting: 0,
    ending: 0,
    totalResultCount: 0,
  });

  // This value keeps track of card selection state
  const [isCardSelected, setIsCardSelected] = useState(false);

  // State management for selection confirmation
  const [confirm, setConfirm] = useState(false);

  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Load Google Maps script
  const { isLoaded: isMapLoaded, loadError } = useLoadScript({
    googleMapsApiKey: `${process.env['NX_DCV2_UI_GOOGLE_API_KEY']}`,
    libraries,
  });

  const searchButtonClickHandler = async () => {
    setShowErrorBanner(false);
    setShopSearchResults(null);
    if (address) {
      setLoading(true);
      setShopResultsToShow(null);
      setError(null);
      let searchResults;
      if (props.searchContext === SearchContext.REPAIR) {
        searchResults = await getRepairShops(address, distance, urlKey, sessionId, authKey);
      } else {
        searchResults = await getRentalShops(address, distance, urlKey, sessionId, authKey);
      }
      setLoading(false);
      if (_get(searchResults, 'status.code', '') === 200) {
        if (searchResults && searchResults?.shops.length > 0) {
          const drpShops: DRPShopResponse = searchResults;

          const preferredShops: ShopsInfo[] = [];
          const otherShops: ShopsInfo[] = [];
          drpShops.shops.forEach((shop) => {
            shop.tier === 'Tier1' ? preferredShops.push(shop) : otherShops.push(shop);
          });
          preferredShops.sort(
            (shop1, shop2) => Number(shop1.distanceInfo.distance ?? 0) - Number(shop2.distanceInfo.distance ?? 0)
          );
          otherShops.sort(
            (shop1, shop2) => Number(shop1.distanceInfo.distance ?? 0) - Number(shop2.distanceInfo.distance ?? 0)
          );
          const fullShopList = [...preferredShops, ...otherShops];
          // Initially there are no shops which are selected. Adding that to the object collection.
          const updatedFullList = fullShopList.map((shop) => {
            return { ...shop, isActive: false };
          });
          setShopSearchResults(updatedFullList);
        }
      } else if (_get(searchResults, 'status.code', '') === 404) {
        // No results
        setError(new Error("'getShops' failed"));
      } else {
        postMessageToParentApp(
          PostMessageCodes.DIGITAL_CLAIM_V2_AUTO_DRP_SHOPS_RETRIEVAL_FAIL_ERROR,
          '',
          searchResults
        );
        setShowErrorBanner(true);
      }
    }
  };

  const shopCardSelectionHandler = (shop: ShopsInfo) => {
    shop.isActive = true;
    setIsCardSelected(true);
    setSelectedMarker(shop);
  };

  const resetSelectedMarker = () => {
    setIsCardSelected(false);
    setConfirm(false);
    setSelectedMarker(null);

    // When a card is no longer selected reset all states, including the 'isActive' property of the object collection
    const updatedFullList =
      shopSearchResults &&
      shopSearchResults.map((shop) => {
        return { ...shop, isActive: false };
      });
    setShopSearchResults(updatedFullList);
  };

  const backNavigationClickHandler = () => {
    resetSelectedMarker();
  };

  const pageSelectionChangeHandler = (pageNumber: number) => {
    getShopsToShow(pageNumber);
  };

  const memoedFetch = React.useMemo(
    () =>
      throttle(
        (
          request: {
            input: string;
            componentRestrictions: { country: string };
          },
          callback: (results?: readonly PlaceType[]) => void
        ) => {
          (autocompleteService.current as any).getPlacePredictions(request, callback);
        },
        200
      ),
    []
  );

  const getShopsToShow = useCallback(
    (page: number) => {
      if (shopSearchResults) {
        const startIndex = (page - 1) * DEFAULT_LIST_SIZE_PER_PAGE;
        const endIndex = startIndex + DEFAULT_LIST_SIZE_PER_PAGE;
        // Below logic handle the case for last page data
        const realEndIndex = endIndex < shopSearchResults.length ? endIndex : shopSearchResults.length;
        const shopsToShow = shopSearchResults.slice(startIndex, realEndIndex);

        setShopResultsToShow(shopsToShow);
        setPaginationData((prevState) => {
          return {
            ...prevState,
            starting: startIndex + 1, // Adding '1' since array index starts at '0'
            ending: realEndIndex,
          };
        });
      }
    },
    [shopSearchResults]
  );

  // UseEffect handler for Pagination
  useEffect(() => {
    const getPaginationData = () => {
      if (shopSearchResults) {
        if (paginationData?.pageCount) {
          const page = Math.trunc(paginationData.starting / DEFAULT_LIST_SIZE_PER_PAGE) + 1;
          getShopsToShow(page);
        } else {
          // Set the page count
          const pageCount = Math.ceil(shopSearchResults.length / DEFAULT_LIST_SIZE_PER_PAGE);
          const initialPaginationData: PaginationData = {
            pageCount,
            starting: 0,
            ending: 0,
            totalResultCount: shopSearchResults.length,
          };
          setPaginationData(initialPaginationData);
          // Set the page content array
          getShopsToShow(1); // Passing 1 by default
        }
      }
    };
    getPaginationData();
  }, [getShopsToShow, paginationData?.pageCount, paginationData.starting, shopSearchResults]);

  // Handle Google Maps script load errors
  useEffect(() => {
    if (loadError) {
      console.error('Failed to load Google Maps script:', loadError);
      setShowErrorBanner(true);
    }
  }, [loadError]);

  // UseEffect handler for search suggestion
  useEffect(() => {
    const placeId = value?.place_id;
    const getFullAddressByPlaceId = async () => {
      if (placeId && isMapLoaded) {
        const fullAddress = await getAddressDetailsByPlaceId(placeId);
        if (fullAddress) {
          setAddress(fullAddress);
        } else {
          // Geocoding failed - show error to user
          console.error('Failed to geocode place_id:', placeId);
          setShowErrorBanner(true);
        }
      }
    };
    getFullAddressByPlaceId();
  }, [value, isMapLoaded]);

  useEffect(() => {
    let active = true;
    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    memoedFetch({ input: inputValue, componentRestrictions: { country: 'us' } }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];
        if (value) {
          newOptions = [value];
        }
        if (results) {
          newOptions = [...newOptions, ...results];
        }
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, memoedFetch]);

  // Loading Indicator messages
  let searchResult = <></>;
  if (!isLoading && shopResultsToShow?.length && !error) {
    searchResult = (
      <ShopInfoCard
        shops={isCardSelected ? shopResultsToShow.filter((shop) => shop.isActive) : shopResultsToShow}
        shopCardSelectionHandler={shopCardSelectionHandler}
        paginationData={paginationData}
        pageSelectionChangeHandler={pageSelectionChangeHandler}
        locationSelection={props.locationSelection}
        confirm={confirm}
        setConfirm={setConfirm}
      />
    );
  }
  if (error) {
    searchResult = <ShopsNotFound />;
  }

  const mapContainerStyle = {
    height: '95%',
    width: '100%',
    border: shopSearchResults ? '1px solid #ccc' : '0px',
  };

  const mapRef = React.useRef<google.maps.Map | null>(null);
  const onMapLoad = React.useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  let mapOptions = undefined;
  if (isMapLoaded) {
    mapOptions = {
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      disableDefaultUI: true,
      zoomControl: true,
      minZoom: 5,
      maxZoom: 20,
    };
  }

  const autoComplete = (
    <Autocomplete
      id="automation_input_search"
      data-testid="automation_input_search"
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      sx={{
        '.MuiAutocomplete-inputRoot': {
          padding: '0rem !important',
          height: theme.custom.styles.defaultInputHeight,
        },
        '& .MuiAutocomplete-clearIndicator': {
          backgroundColor: '#FFF',
          marginRight: '-.4rem',
        },
      }}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      forcePopupIcon={false}
      onChange={(event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          placeholder="Search"
          inputProps={{
            ...params.inputProps,
            style: {
              ...theme.custom.styles.secondaryDescription,
              height: '50%',
            },
          }}
        />
      )}
      renderOption={({ key, ...props }, option) => {
        const matches = option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: any) => [match.offset, match.offset + match.length])
        );

        return (
          <li key={`listitem-${key}`} {...props}>
            <Grid container alignItems="center">
              <Grid item>
                <Box component={LocationOnIcon} sx={{ color: 'text.secondary', mr: 2 }} />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{
                      fontWeight: part.highlight ? 700 : 400,
                    }}
                  >
                    {part.text}
                  </span>
                ))}
                <Typography variant="body2" color="text.secondary" sx={theme.custom.styles.secondaryDescription}>
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );

  const searchControlsOnly = (
    <Grid container spacing={2}>
      <Grid item xs={9} sx={{ marginTop: '0px' }}>
        <Typography
          component="label"
          htmlFor="search"
          sx={{
            ...theme.custom.styles.secondaryDescription,
            paddingLeft: { xs: 0, md: '1rem' },
          }}
        >
          Search
        </Typography>
        <Box sx={{ paddingLeft: { xs: 0, md: '1rem' } }}>{autoComplete}</Box>
      </Grid>
      <Grid item xs={3}>
        <Box
          sx={{
            marginTop: '1.3rem',
            minWidth: 20,
            paddingRight: {
              sm: '0rem',
              xs: '0rem',
            },
            paddingLeft: {
              sm: '0rem',
              xs: '0rem',
              lg: '1rem',
              md: '1rem',
              xl: '1rem',
            },
          }}
        >
          <SearchButton searchButtonClickHandler={searchButtonClickHandler} />
        </Box>
      </Grid>
      {/* Progress Indicator */}
      {isLoading && (
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            paddingLeft: '1rem',
            paddingBottom: '1rem',
            width: '100%',
          }}
        >
          <Box sx={{ width: '100%' }}>
            <LoadingIndicator message={'Looking for nearby shops'} />
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const repairProgramDescription = (
    <Typography
      sx={{
        p: '1rem',
        borderRight: { xs: '0px solid #ccc', md: '2px solid #ccc' },
        borderLeft: { xs: '0px solid #ccc', md: '2px solid #ccc' },
      }}
    >
      Our <strong>{props.copy.repairServiceName}</strong> takes the stress out of choosing a repair shop. Search for and
      connect with a trusted, customer-friendly shop near you.
    </Typography>
  );

  const mapView = (
    <Box
      sx={{
        flexGrow: 1,
        [theme.breakpoints.down('md')]: {
          height: shopSearchResults ? '25%' : '0%',
          width: '100%',
          minHeight: shopSearchResults ? '30vh' : '0vh',
        },
        [theme.breakpoints.up('md')]: {
          paddingLeft: '1rem',
          height: shopSearchResults ? '100%' : '0%',
          width: '100%',
          minHeight: shopSearchResults ? '60vh' : '0vh',
        },
      }}
    >
      {isMapLoaded && shopResultsToShow && (
        <GoogleMap
          id="map"
          zoom={DEFAULT_ZOOM}
          mapContainerStyle={mapContainerStyle}
          center={{
            lat: shopResultsToShow ? +shopResultsToShow[0].address.coordinates.latitude : 0,
            lng: shopResultsToShow ? +shopResultsToShow[0].address.coordinates.longitude : 0,
          }}
          onLoad={onMapLoad}
          options={mapOptions}
        >
          {shopResultsToShow &&
            !isLoading &&
            shopResultsToShow.map((shop) => (
              <Marker
                key={shop.id}
                position={{
                  lat: +shop.address.coordinates.latitude,
                  lng: +shop.address.coordinates.longitude,
                }}
                onClick={() => {
                  setSelectedMarker(shop);
                }}
              />
            ))}
          {selectedMarker ? (
            <InfoWindow
              position={{
                lat: +selectedMarker.address.coordinates.latitude,
                lng: +selectedMarker.address.coordinates.longitude,
              }}
              onCloseClick={() => {
                setSelectedMarker(null);
              }}
              options={{
                pixelOffset: new google.maps.Size(0, -45),
                disableAutoPan: false,
              }}
            >
              <Address key={selectedMarker.id} buttonEnabled={false} shop={selectedMarker} />
            </InfoWindow>
          ) : null}
        </GoogleMap>
      )}
    </Box>
  );

  return (
    <Grid container>
      {/* Error banner */}
      <Grid
        item
        order={0}
        xs={12}
        sx={{
          width: '100%',
          p: '1rem',
        }}
      >
        {showErrorBanner && <ErrorBanner />}
      </Grid>

      {/* Left column: Search controls and results (desktop) */}
      <Grid
        item
        order={{ xs: 1, md: 1 }}
        xs={12}
        md={6}
        sx={{
          px: '1rem',
        }}
      >
        {/* Search controls only when no card is selected */}
        {!isCardSelected && searchControlsOnly}
        {/* Desktop: Results and back navigation */}
        <Hidden mdDown>
          {isCardSelected && <BackNavigation backNavigationClickHandler={backNavigationClickHandler} />}
          {searchResult}
        </Hidden>
      </Grid>

      {/* Right column: Map (desktop) */}
      {isMapLoaded && shopResultsToShow && (
        <Grid
          item
          order={{ xs: 3, md: 2 }}
          xs={12}
          md={6}
          sx={{
            p: '1rem',
          }}
        >
          {mapView}
        </Grid>
      )}

      {/* Program Description & Phone number */}
      {!shopResultsToShow && props.searchContext === SearchContext.REPAIR && (
        <Grid item order={2} xs={12} md={6} container justifyContent="left" alignItems="stretch">
          <Grid item md={6}>
            {repairProgramDescription}
          </Grid>
          <Grid item md={6}></Grid>
        </Grid>
      )}
      {!shopResultsToShow && props.searchContext === SearchContext.RENTAL && (
        <Grid item order={2} xs={12} container></Grid>
      )}

      {/* Mobile: Results and back navigation */}
      <Hidden mdUp>
        <Grid
          item
          order={6}
          xs={12}
          sx={{
            pr: '2rem',
          }}
        >
          {isCardSelected && <BackNavigation backNavigationClickHandler={backNavigationClickHandler} />}
          {searchResult}
        </Grid>
      </Hidden>
    </Grid>
  );
};

export default Search;
